import React, { useState, useEffect } from 'react';
import {
    AccordionSummary,
    AccordionDetails,
    Grid,
    Button,
    TextField,
    IconButton,
    withTheme,
    InputLabel,
    Accordion
} from '@material-ui/core';
import moment from 'moment';
import { Formik, Form } from 'formik';
import DatePickerDropdown from '../../../components/atom/DatePickerDropdown';
import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall } from '../../../hooks/useSwaggerClient';
import { ChevronDownIcon, ErrorIcon } from '../../../icons';
import { RouterPrompt } from '../../../components/atom/RouterPrompt';

const CustomBookingForm = withTheme(
    ({ onCancel, onSubmit, initialValues, theme, submitCustomBooking, customerId }) => {
        const validate = values => {
            const errors = {};

            if (moment(values.activity_end).isBefore(values.activity_start)) {
                errors.dateError = 'Start date cannot be after end date!';
            }
            if (!customerId) {
                errors.customerIdError = 'For custom bookings, you must select a Guest!';
            }

            return errors;
        };
        return (
            <Formik onSubmit={onSubmit} initialValues={initialValues} validate={validate}>
                {({ isSubmitting, values, setFieldValue, setValues, isValid, errors, dirty }) => (
                    <>
                        <RouterPrompt when={!!dirty && !submitCustomBooking} />
                        <Form>
                            {Object.keys(errors).length > 0 && (
                                <Grid
                                    item
                                    container
                                    spacing={2}
                                    xs={12}
                                    style={{
                                        color: theme.palette.error.main
                                    }}
                                >
                                    <Grid item>
                                        <ErrorIcon color="error" />
                                    </Grid>
                                    <Grid item>
                                        {Object.values(errors).map(e => (
                                            <div>{e}</div>
                                        ))}
                                    </Grid>
                                </Grid>
                            )}
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Voucher #"
                                        value={values?.voucher_number}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Vendor Confirmation #"
                                        value={values.vendor_code}
                                        onChange={ev =>
                                            setFieldValue('vendor_code', ev.target.value)
                                        }
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <div style={{ width: 260 }} className="golf-picker">
                                        <InputLabel style={{ marginBottom: '3px' }} shrink>
                                            Activity Start Date & Time:
                                        </InputLabel>
                                        <DatePickerDropdown
                                            date={values.activity_start}
                                            id="bookingPanelStartDate"
                                            setDate={newDate => {
                                                const timeDiff = moment(
                                                    initialValues.activity_end
                                                ).diff(initialValues.activity_start);

                                                setValues({
                                                    ...values,
                                                    activity_start: moment(newDate).format(
                                                        'YYYY-MM-DD HH:mm:ss'
                                                    ),
                                                    activity_end: moment(newDate)
                                                        .add(timeDiff)
                                                        .format('YYYY-MM-DD HH:mm:ss')
                                                });
                                            }}
                                            enableTimePicker
                                            inputFormat="ddd MMM DD, YYYY [at] h:mm A"
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Activity End Date & Time"
                                        value={moment(values.activity_end).format(
                                            'ddd MMM DD, YYYY [at] h:mm A'
                                        )}
                                        disabled
                                    />
                                </Grid>
                                <Grid container item alignItems="center">
                                    <Button
                                        style={{
                                            color: theme.palette.error.main,
                                            marginRight: '10px'
                                        }}
                                        onClick={onCancel}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting || !isValid}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    </>
                )}
            </Formik>
        );
    }
);

const CustomBooking = ({
    cartitem,
    cartitemIndex,
    setHasCustomBooking,
    setCustomBooking,
    updateCartItem,
    theme,
    customerId
}) => {
    const [expanded, setExpanded] = useState(true);
    const { notifySuccess, notifyError } = useNotify();
    const [submitCustomBooking, setSubmitCustomBooking] = useState(false);

    useEffect(() => {
        if (!customerId) {
            notifyError('For custom bookings, you must select a Guest!');
            return;
        }
        if (!cartitem.customBooking?.id) {
            const req = doSwaggerCall('bookings', 'createCustomBooking', {
                data: {
                    activityId: cartitem.activity_id,
                    customBookingData: {
                        activity_start: cartitem.start_time,
                        activity_end: cartitem.end_time,
                        guest_id: customerId
                    }
                }
            });
            req.then(res => {
                setCustomBooking(cartitemIndex, res);
                notifySuccess('Custom booking created');
            }).catch(e => {
                console.log(e);
                notifyError(e.response.data.message);
            });
        }
    }, [customerId, notifySuccess]);

    const onSubmit = (values, { setSubmitting }) => {
        setSubmitting(true);
        const req = doSwaggerCall('bookings', 'updateCustomBooking', {
            customBookingId: values.id,
            customBookingData: {
                activity_start: values.activity_start,
                activity_end: values.activity_end,
                vendor_code: values.vendor_code,
                guest_id: customerId
            }
        });
        setExpanded(false);
        req.then(res => {
            updateCartItem(cartitemIndex, {
                ...cartitem,
                start_time: res.activity_start,
                end_time: res.activity_end,
                customBooking: res
            });
            setSubmitting(false);
            setSubmitCustomBooking(true);
            notifySuccess('Custom booking updated');
        }).catch(e => {
            setExpanded(true);
            notifyError(e.response.data.message);
            setSubmitting(false);
        });
    };

    const onCancel = () => {
        const req = doSwaggerCall('bookings', 'deleteCustomBooking', {
            customBookingId: cartitem.customBooking.id
        });
        req.then(() => {
            setHasCustomBooking(cartitemIndex, false);
            notifySuccess('Custom booking deleted');
        }).catch(e => {
            notifyError(e.response.data.message);
        });
    };
    return (
        <Grid item xs={12} className="mb-2">
            <Accordion
                defaultExpanded
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
                style={{
                    borderLeft: `6px solid ${theme.palette.primary.main}`
                }}
                elevation={0}
            >
                <AccordionSummary
                    expandIcon={
                        <IconButton>
                            <ChevronDownIcon />
                        </IconButton>
                    }
                >
                    <Grid container>
                        <Grid item xs={12}>
                            <h4>Custom Booking</h4>
                        </Grid>
                        {!expanded && (
                            <>
                                <Grid item xs={6}>
                                    <span
                                        className="mb-2"
                                        style={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)' }}
                                    >
                                        Voucher #:
                                    </span>
                                    <br />
                                    <span style={{ fontSize: '18px' }}>
                                        {cartitem?.customBooking?.voucher_number}
                                    </span>
                                </Grid>
                                <Grid item xs={6}>
                                    <span
                                        className="mb-2"
                                        style={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)' }}
                                    >
                                        Vendor Confirmation #:
                                    </span>
                                    <br />
                                    <span style={{ fontSize: '18px' }}>
                                        {cartitem?.customBooking?.vendor_code}
                                    </span>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container>
                        {cartitem.customBooking && (
                            <CustomBookingForm
                                initialValues={cartitem.customBooking}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitCustomBooking={submitCustomBooking}
                                customerId={customerId}
                            />
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

export default withTheme(CustomBooking);
